import { useEffect, useState } from 'preact/hooks';
import { api, ApiError } from '../api.js';
import { t } from '../i18n/index.js';
import { Logo } from '../components/Logo.js';
import { Footer } from '../components/Footer.js';
import { NewPasswordFields, newPasswordError } from '../components/PasswordFields.js';
import { limits, type MeUser } from '../../shared/protocol.js';
import { INVITE_CODE_LENGTH, normalizeInviteCode } from '../../shared/registration.js';

/**
 * Public registration pages (decision 2026-09-28):
 *  - link:  join link from the administrator, username given → display name + password
 *  - reset: password reset link → new password
 *  - code:  invite code (from a QR code the code is filled in and cannot be changed)
 *           → username, display name and password
 * The account is signed in right after saving.
 */
export type JoinMode = 'link' | 'reset' | 'code';

type Checked = { username?: string; expiresAt: number } | null;

const errorText = (err: unknown) => t.errors[err instanceof ApiError ? err.code : 'network'];

function formatDateTime(ms: number, withTime: boolean): string {
  return new Date(ms).toLocaleString('fi-FI', withTime ? { dateStyle: 'short', timeStyle: 'short' } : { dateStyle: 'short' });
}

export function JoinView(props: { mode: JoinMode; param: string | null; onLogin: (user: MeUser) => void }) {
  const { mode, param } = props;
  // Link pages and a QR code carry the token / code; it is checked before the form is shown.
  const [checked, setChecked] = useState<Checked>(null);
  const [checkError, setCheckError] = useState<string | null>(null);
  const [code, setCode] = useState(param ? normalizeInviteCode(param) : '');
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const codeLocked = mode === 'code' && param !== null;

  useEffect(() => {
    if (mode !== 'code') {
      if (!param) return setCheckError(t.errors.invalid_link);
      api<{ purpose: 'join' | 'reset'; username: string; expiresAt: number }>('POST', '/api/join/link/check', { token: param })
        .then((r) => {
          if (r.purpose !== (mode === 'link' ? 'join' : 'reset')) setCheckError(t.errors.invalid_link);
          else setChecked({ username: r.username, expiresAt: r.expiresAt });
        })
        .catch((err) => setCheckError(errorText(err)));
    } else if (param) {
      api<{ code: string; expiresAt: number }>('POST', '/api/join/code/check', { code: param })
        .then((r) => setChecked({ expiresAt: r.expiresAt }))
        .catch((err) => setCheckError(errorText(err)));
    } else {
      // Typed code: only check that registration is open.
      api<{ codeRegistration: boolean }>('GET', '/api/join/status')
        .then((r) => !r.codeRegistration && setCheckError(t.errors.registration_closed))
        .catch((err) => setCheckError(errorText(err)));
    }
  }, [mode, param]);

  const submit = async (e: Event) => {
    e.preventDefault();
    const pwError = newPasswordError(password, repeat, t.join.mismatch);
    if (pwError) return setError(pwError);
    setBusy(true);
    setError(null);
    try {
      const r =
        mode === 'link'
          ? await api<{ user: MeUser }>('POST', '/api/join/link', { token: param, displayName, password })
          : mode === 'reset'
            ? await api<{ user: MeUser }>('POST', '/api/join/reset', { token: param, password })
            : await api<{ user: MeUser }>('POST', '/api/join/code', { code: normalizeInviteCode(code), username, displayName, password });
      props.onLogin(r.user);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const title = mode === 'link' ? t.join.linkTitle : mode === 'reset' ? t.join.resetTitle : t.join.codeTitle;
  const intro = mode === 'link' ? t.join.linkIntro : mode === 'reset' ? t.join.resetIntro : t.join.codeIntro;
  const needsCheck = mode !== 'code' || codeLocked;
  const ready = !checkError && (!needsCheck || checked !== null);

  return (
    <div class="center page-with-footer">
      <form class="card login join" onSubmit={submit}>
        <h1 class="login-logo">
          <Logo size="large" animated />
        </h1>
        <h2 class="center-text">{title}</h2>
        {checkError ? (
          <p class="error" role="alert">
            {checkError}
          </p>
        ) : !ready ? (
          <p class="subtitle" aria-busy="true">
            {t.join.checking}
          </p>
        ) : (
          <>
            <p class="subtitle">{intro}</p>
            {mode === 'code' && (
              <label>
                {t.join.code}
                <input
                  class="code-input"
                  value={code}
                  onInput={(e) => setCode(e.currentTarget.value.toUpperCase())}
                  readOnly={codeLocked}
                  maxLength={INVITE_CODE_LENGTH + 2}
                  autocomplete="off"
                  autocapitalize="characters"
                  spellcheck={false}
                  required
                  autofocus={!codeLocked}
                />
                {!codeLocked && <small>{t.join.codeHint}</small>}
              </label>
            )}
            {mode === 'code' ? (
              <label>
                {t.join.username}
                <input
                  value={username}
                  onInput={(e) => setUsername(e.currentTarget.value)}
                  pattern={limits.usernamePattern.slice(1, -1)}
                  autocomplete="username"
                  autocapitalize="none"
                  spellcheck={false}
                  required
                  autofocus={codeLocked}
                />
                <small>{t.join.usernameHint}</small>
              </label>
            ) : (
              <label>
                {t.join.username}
                <input value={checked?.username ?? ''} autocomplete="username" readOnly />
              </label>
            )}
            {mode !== 'reset' && (
              <label>
                {t.join.displayName}
                <input
                  value={displayName}
                  onInput={(e) => setDisplayName(e.currentTarget.value)}
                  minLength={limits.displayNameMin}
                  maxLength={limits.displayNameMax}
                  autocomplete="nickname"
                  required
                  autofocus={mode === 'link'}
                />
                <small>{t.join.displayNameHint}</small>
              </label>
            )}
            <NewPasswordFields
              value={password}
              repeat={repeat}
              onValue={setPassword}
              onRepeat={setRepeat}
              label={t.join.password}
              repeatLabel={t.join.repeat}
              autofocus={mode === 'reset'}
            />
            {checked && (
              <p class="hint center-text">
                {mode === 'code' ? t.join.codeValid(formatDateTime(checked.expiresAt, false)) : t.join.linkValid(formatDateTime(checked.expiresAt, true))}
              </p>
            )}
            {error && (
              <p class="error" role="alert">
                {error}
              </p>
            )}
            <button class="btn btn-primary" type="submit" disabled={busy}>
              {busy ? t.join.submitting : mode === 'reset' ? t.join.submitReset : t.join.submitJoin}
            </button>
            {mode !== 'reset' && (
              <p class="hint center-text">
                <a href="tietosuoja.html">{t.join.privacy}</a>
              </p>
            )}
          </>
        )}
        <a class="btn btn-ghost" href="#/">
          {t.join.backToLogin}
        </a>
      </form>
      <Footer />
    </div>
  );
}
